import { PAYMENT_METHOD_LABELS, formatBps } from '@shared/ledger';

import { InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCommissionRates } from '@/lib/queries';
import { toMessage } from '@/lib/api';

/**
 * What the trader earns, per method.
 *
 * Rates are basis points on the wire and are never shown as such: `formatBps`
 * turns 250 into «2,50 %» once, in the shared package, so this screen and the
 * admin console cannot round the same rate differently.
 */
export function CommissionRates() {
  const rates = useCommissionRates();

  if (rates.isPending) {
    return (
      <div className="px-4 py-4" role="status" aria-busy="true">
        <span className="sr-only">Загружаю ставки комиссии</span>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-2.5 h-4 w-28" />
      </div>
    );
  }

  if (rates.isError) {
    return (
      <div className="p-4">
        <InlineError
          message={toMessage(rates.error, 'Не удалось загрузить ставки комиссии.')}
          onRetry={() => void rates.refetch()}
        />
      </div>
    );
  }

  if (rates.data.length === 0) {
    return (
      <p className="px-4 py-4 text-sm text-fg-secondary">
        Ставки пока не назначены. Их устанавливает администратор.
      </p>
    );
  }

  return (
    <div className="px-4 py-4">
      <dl className="divide-y divide-line">
        {rates.data.map((rate) => (
          <div key={rate.method} className="flex items-baseline justify-between gap-4 py-2 first:pt-0 last:pb-0">
            <dt className="text-base text-fg">{PAYMENT_METHOD_LABELS[rate.method]}</dt>
            <dd className="num text-md font-medium text-fg">{formatBps(rate.rateBps)}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-3 max-w-prose text-sm leading-normal text-fg-secondary">
        Столько вы получаете с каждого подтверждённого платежа. Комиссия зачисляется в «Доступно к
        выводу» в момент подтверждения, отдельно от залога.
      </p>
    </div>
  );
}
